import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getCodeHistory, 
  getFavoriteCodes, 
  deleteCode, 
  toggleFavorite 
} from '../../../services/codegen.service';
import { showSuccess, showError } from '../../../services/notification.service';
import ConfirmDialog from '../../shared/ConfirmDialog';
import './CodeHistory.css';

const CodeHistory = ({ showFavorites = false }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, codeId: null });
  const navigate = useNavigate();

  const loadCodes = async () => {
    try {
      setLoading(true);
      const data = showFavorites ? await getFavoriteCodes() : await getCodeHistory();
      
      if (data.success) {
        setCodes(data.codes || []);
      } else {
        setError('Failed to load code history');
      }
    } catch (err) {
      setError('Error loading code history');
      console.error('Error loading code history:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFavorites]);

  const handleCodeClick = (codeId) => {
    navigate(`/tools/code-generator/codes/${codeId}`);
  };

  const handleDeleteCode = (codeId, event) => {
    event.stopPropagation();
    setConfirmDialog({ isOpen: true, codeId: codeId });
  };

  const confirmDeleteCode = async () => {
    const codeId = confirmDialog.codeId;
    setConfirmDialog({ isOpen: false, codeId: null });

    try {
      const data = await deleteCode(codeId);
      
      if (data.success) {
        setCodes(codes.filter(code => code._id !== codeId));
        showSuccess('Code deleted successfully');
      } else {
        const errorMsg = 'Failed to delete code';
        setError(errorMsg);
        showError(errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Error deleting code';
      setError(errorMsg);
      showError(errorMsg);
      console.error('Error deleting code:', err);
    }
  };

  const handleToggleFavorite = async (codeId, currentStatus, event) => {
    event.stopPropagation();
    
    try {
      const data = await toggleFavorite(codeId, !currentStatus);
      
      if (data.success) {
        setCodes(codes.map(code => 
          code._id === codeId 
            ? { ...code, is_favorite: !currentStatus }
            : code
        ).filter(code => !showFavorites || code.is_favorite));
        
        showSuccess(!currentStatus ? 'Added to favorites' : 'Removed from favorites');
      } else {
        const errorMsg = 'Failed to update favorite status';
        setError(errorMsg);
        showError(errorMsg);
      }
    } catch (err) {
      const errorMsg = 'Error updating favorite status';
      setError(errorMsg);
      showError(errorMsg);
      console.error('Error updating favorite:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const capitalize = (str) => {
    if (!str) return '';
    // Special cases
    const specialCases = {
      'python': 'Python',
      'typescript': 'TypeScript',
      'javascript': 'JavaScript',
      'java': 'Java',
      'csharp': 'C#',
      'go': 'Go'
    };
    return specialCases[str.toLowerCase()] || str.charAt(0).toUpperCase() + str.slice(1);
  };

  if (loading) {
    return (
      <div className="code-history-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading {showFavorites ? 'favorites' : 'history'}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="code-history-container">
      <div className="code-history-header">
        <h2>{showFavorites ? '⭐ Favorite Code' : '📚 Code History'}</h2>
        <button className="btn-refresh" onClick={loadCodes}>
          🔄 Refresh
        </button>
      </div>

      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
        </div>
      )}

      {!error && codes.length === 0 && (
        <div className="no-codes">
          <p>📭 No {showFavorites ? 'favorites' : 'code history'} found</p>
          <p className="hint">
            {showFavorites 
              ? 'Mark code generations as favorites to see them here' 
              : 'Generate code to see history here'}
          </p>
        </div>
      )}

      {!error && codes.length > 0 && (
        <div className="codes-grid">
          {codes.map((code) => (
            <div
              key={code._id}
              className="code-card"
              onClick={() => handleCodeClick(code._id)}
            >
              <div className="code-card-header">
                <div className="code-info">
                  <h3 className="code-title">
                    {code.title || `${capitalize(code.language) || 'N/A'} Code`}
                  </h3>
                </div>
                <div className="code-actions">
                  <button
                    className={`btn-favorite ${code.is_favorite ? 'favorited' : ''}`}
                    onClick={(e) => handleToggleFavorite(code._id, code.is_favorite, e)}
                    title={code.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    {code.is_favorite ? '⭐' : '☆'}
                  </button>
                  <button
                    className="btn-delete-card"
                    onClick={(e) => handleDeleteCode(code._id, e)}
                    title="Delete code"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              <div className="code-card-body">
                <p className="code-language-info">
                  💻 {capitalize(code.language) || 'N/A'}
                  {code.framework && ` - ${code.framework}`}
                </p>
                <p className="code-requirements" title={code.requirements}>
                  {(code.requirements || 'No requirements specified').substring(0, 150)}
                  {(code.requirements || '').length > 150 ? '...' : ''}
                </p>
                
                <div className="code-meta">
                  {code.files_count > 0 && (
                    <div className="files-count">
                      📁 {code.files_count} file{code.files_count !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>

              <div className="code-date">
                <span>Generated: {formatDate(code.created_at || code.generated_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Code"
        message="Are you sure you want to delete this code generation? This action cannot be undone."
        type="danger"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteCode}
        onCancel={() => setConfirmDialog({ isOpen: false, codeId: null })}
      />
    </div>
  );
};

export default CodeHistory;
